import { format, isValid } from 'date-fns';
import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Modal, Row, Col } from 'react-bootstrap';
import { connect } from "react-redux";
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from "recompose";
import { routes, dateFormats } from '../../../../../constants';
import { mapRecommendedContent, mapRecommendedCourses, validators } from '../../../../../services';
import { businessSelector } from '../../../../../state/modules/business';
import { Wizard } from '../../../../components';
import AddMembers from './add-members';
import Review from './review';
import SetContent from './set-content';
import SetTask from './set-task';
import { footers } from './set-task-content-footer';

const wizardConfiguration = ({
  ...props
}) => ({
  name: 'set-task-content-wizard',
  currentStep: props.currentStep,
  steps: [
    {
      name: 'Set task',
      title: <h1>
        Set Task
      </h1>,
      component: <SetTask {...props} />,
      footer: <footers.step1 {...props} />
    },
    {
      name: 'Set content',
      title: <h1>
        Set Content
      </h1>,
      component: <SetContent {...props} />,
      footer: <footers.step2 {...props} />
    },
    {
      name: 'Add individuals and teams',
      title: <h1>
        Add Members
      </h1>,
      component: <AddMembers {...props} />,
      footer: <footers.step3 {...props} />
    },
    {
      name: 'Review',
      title: <h1>
        Review Task
      </h1>,
      component: <Review {...props} />,
      footer: <footers.step4 {...props} />
    }
  ]
})

const SetTaskContent = (props) => {
  const showModal = props.showModal;
  const onHideModal = props.onHideModal;
  const goToTaskSettingStep = () => {
    props.stepTo(0);
    onHideModal();
  }

  const taskDueDate = isValid(props.task.dueDate.value)
    ? format(props.task.dueDate.value, dateFormats.ShortYearDate)
    : ''; 

  const contentExpireMessages = props.expireBeforeDueDateContents.map((content) => (
    `${content.title} will expire on ${format(new Date(content.expiryDate), dateFormats.ShortYearDate)}`
  ));

  return (
  <div id="set-task-content">
    <Wizard config={wizardConfiguration(props)} />
    <Modal show={showModal} onHide={onHideModal}>
      <Modal.Header>
        <Modal.Title componentClass='h2' className='text-center'>
          Cannot save your task contents
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='text-center bottom-gap'>
        The following contents will expire before your task due date ({taskDueDate}):
        <br />
        {
          contentExpireMessages.map((message) => (<p>{message}</p>))
        }
        Please select a new content item, or bring forward the task due date to end while the content is still active. 
        If the content is something you have uploaded to your Business Library, you can extend the expiry date of it&nbsp;
        <a href={routes.app.library.root}>here</a>.
      </Modal.Body>
      <Modal.Footer className='text-center confirmation-footer'>
        <Row>
          <Col xs={12} onClick={goToTaskSettingStep}>
            <Button>
              Update task due date
            </Button>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Button onClick={onHideModal} bsStyle='plain'>
              Choose different content
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  </div>
)};

const mapStateToProps = (state) => ({
  business: businessSelector(state),
  mapContent: mapRecommendedContent(state.industry.cpdCategorys, state.publisher.publishers, businessSelector(state)),
  mapCourses: mapRecommendedCourses(state.industry.cpdCategorys, state.publisher.publishers)
});

const mapDispatchToProps = (dispatch) => ({
});

const getDataSchema = (task, mapContent, mapCourses, business) => ({
  validation: undefined,
  name: {
    value: task ? he.decode(task.name) : undefined,
    validation: undefined,
    validator: validators.notEmpty("Please enter a title for your task.")
  },
  description: {
    value: task ? he.decode(task.description) : undefined,
    validation: undefined,
    validator: validators.notEmpty("Please give your task a description.")
  },
  dueDate: {
    value: task ? new Date(task.dueDate) : undefined,
    validation: undefined,
    validator: validators.future("Date must be in the future. ")
  },
  isArchived: {
    value: task ? task.isArchived : false,
    validation: undefined,
    validator: undefined
  },
  searchTiles: {
    value: task && task.contents ? mapContent(task.contents.filter(
      ({ cpdCategorys, businessId }) => cpdCategorys.length > 0 && businessId !== business.id
    )).concat(mapCourses(task.courses)) : [],
    validation: undefined,
    validator: validators.notEmpty("Please select at least one content item")
  },
  businessLibraryContent: {
    value: task && task.contents ? mapContent(task.contents.filter(
      ({ businessId }) => businessId === business.id
    )) : [],
    validation: undefined,
    validator: undefined
  },
  users: {
    value: task ? fp.map(({ userId }) => userId)(task.users) : [],
    validation: undefined,
    validator: validators.notEmpty("Please select at least one user")
  },
  teams: {
    value: [],
    validation: undefined,
    validator: undefined
  },
  excludeUser: {
    value: false,
    validation: undefined,
    validator: undefined
  },
  selectedIds: {
    value: task ? task.searchTileIds : [],
    validation: undefined,
    validator: undefined
  }
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    ({ task, mapContent, mapCourses, business }) => ({
      taskEditing: task,
      task: getDataSchema(task, mapContent, mapCourses, business),
      currentStep: task ? 3 : 0,
      maxStep: task ? 3 : 0,
      showModal: false,
      expireBeforeDueDateContents: [],
    }),
    {
      updateTask: ({ task }) => (fieldName, value) => ({
        task: {
          ...task,
          [fieldName]: value
        }
      }),
      stepForward: ({ currentStep, maxStep }) => () => ({
        currentStep: currentStep + 1,
        maxStep: fp.max([currentStep + 1, maxStep])
      }),
      stepBack: ({ currentStep }) => () => ({
        currentStep: currentStep - 1
      }),
      stepTo: () => (step) => ({
        currentStep: step
      }),
      onShowModal: () => () => ({
        showModal: true
      }),
      onHideModal: () => () => ({
        showModal: false
      }),
      setExpireBeforeDueDateContents: () => (newContents) => ({
        expireBeforeDueDateContents: newContents
      })
    }
  ),
  withProps(
    ({ maxStep }) => ({
      reviewing: maxStep === 3
    })
  ),
  withHandlers({
    onTaskEdited: ({ task, updateTask }) => (fieldName, value) => {
      const { validator } = task[fieldName]
      const validation = validator ? validator(value) : undefined
      if (validation) {
        updateTask(fieldName, {
          ...task[fieldName],
          value,
          validation: validation
        })
      } else {
        updateTask(fieldName, {
          ...task[fieldName],
          value,
          validation: undefined
        })
      }
    },
    onTaskSubmitted: ({ task, updateTask, onTaskEdited }) => () => {
      const validations = {
        name: task.name.validation,
        description: task.description.validation,
        dueDate: task.dueDate.validation,
        searchTiles: task.searchTiles.validation,
        users: task.users.validation,
        teams: task.teams.validation
      }
      const validation = fp.flow(
        fp.values,
        fp.filter(fp.identity),
        (list) => list.length > 0 ? validations : undefined
      )(validations)
      updateTask('validation', validation)
      return validation
    }
  }),
  lifecycle({
    componentDidMount() {
      const {
        task,
        onTaskEdited
      } = this.props;
      onTaskEdited('name', task.name.value)
      onTaskEdited('description', task.description.value)
      onTaskEdited('dueDate', task.dueDate.value)
    }
  }),
)(SetTaskContent)


// WEBPACK FOOTER //
// ./src/containers/pages/app/tasks/partials/set-task-content.js